import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, Copy, Star, Store, Maximize2, X, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { api, img, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { QtyStepper } from "../components/Overlays";
import { ProductGrid } from "../components/ProductCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { NotFound } from "./Catalog";
import { ReviewSection } from "../components/reviews/ReviewSection";

import { tx, useLang, txf, soldLabel } from "../lib/i18n";
import { DeliveryEstimate } from "../components/CommerceTools";
const Lightbox = ({ images, active, setActive, onClose, title }) => { useLang();
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const n = images.length;
  const go = (d) => { setZoom(false); setActive((active + d + n) % n); };
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => { window.removeEventListener("keydown", k); };
  });
  const move = (e) => { const r = e.currentTarget.getBoundingClientRect(); setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); };
  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/95" role="dialog" aria-label={tx("Photo preview")} data-testid="gallery-lightbox">
      <div className="flex items-center justify-between p-4 text-sm text-[var(--mv-muted)]">
        <span data-testid="lightbox-counter">{active + 1} / {tx(n)}</span>
        <div className="flex gap-2">
          <button onClick={() => setZoom(!zoom)} className="grid h-10 w-10 place-items-center rounded-full mv-glass" aria-label={tx(zoom ? "Zoom out" : "Zoom in")} data-testid="lightbox-zoom-button">{tx(zoom ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />)}</button>
          <button onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full mv-glass" aria-label={tx("Close")} data-testid="lightbox-close-button"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="relative flex-1 overflow-hidden" onMouseMove={zoom ? move : undefined} onClick={() => setZoom(!zoom)}>
        <img src={img(images[active])} alt={title} data-testid="lightbox-image" style={{ transform: zoom ? "scale(2.2)" : "scale(1)", transformOrigin: origin }}
          className={`h-full w-full object-contain transition-transform duration-200 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`} />
      </div>
      {tx(n > 1 && <>
        <button onClick={() => go(-1)} className="absolute left-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full mv-glass" aria-label={tx("Previous image")} data-testid="lightbox-prev-button"><ChevronLeft className="h-6 w-6" /></button>
        <button onClick={() => go(1)} className="absolute right-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full mv-glass" aria-label={tx("Next image")} data-testid="lightbox-next-button"><ChevronRight className="h-6 w-6" /></button>
      </>)}
    </div>
  );
};

const Gallery = ({ images, active, setActive, title, aiImage }) => { useLang();
  const [full, setFull] = useState(false);
  const n = images.length;
  const go = (d) => setActive((active + d + n) % n);
  const isCut = (u) => u?.includes("/hero/");
  return (
    <div data-testid="product-gallery">
      <div className="relative aspect-square overflow-hidden rounded-3xl border border-[var(--mv-border)] mv-card-bg">
        <img src={img(images[active])} alt={title} onClick={() => setFull(true)} className={`h-full w-full cursor-zoom-in ${isCut(images[active]) ? "object-contain p-12" : "object-cover"}`} data-testid="gallery-main-image" />
        {tx(aiImage && <span className="absolute bottom-4 left-4 rounded-md bg-black/60 px-2 py-1 text-[10px] text-[var(--mv-muted)]" data-testid="gallery-ai-image-label">{tx("Representative image")}</span>)}
        <button onClick={() => setFull(true)} aria-label={tx("Full-screen preview")} data-testid="gallery-fullscreen-button" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full mv-glass hover:border-[var(--mv-teal)]"><Maximize2 className="h-4 w-4" /></button>
        {tx(n > 1 && (
          <>
            <button onClick={() => go(-1)} data-testid="gallery-prev-button" aria-label={tx("Previous image")} className="absolute left-4 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full mv-glass hover:border-[var(--mv-teal)]"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => go(1)} data-testid="gallery-next-button" aria-label={tx("Next image")} className="absolute right-4 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full mv-glass hover:border-[var(--mv-teal)]"><ChevronRight className="h-5 w-5" /></button>
            <span className="absolute bottom-4 right-4 rounded-full mv-glass px-3 py-1 text-xs" data-testid="gallery-counter">{active + 1} / {tx(n)}</span>
          </>
        ))}
      </div>
      {tx(n > 1 && (
        <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar">
          {images.map((u, i) => (
            <button key={u + i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
              className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 mv-card-bg ${i === active ? "border-[var(--mv-teal)]" : "border-transparent"}`}>
              <img src={img(u)} alt="" className={`h-full w-full ${isCut(u) ? "object-contain p-2" : "object-cover"}`} />
            </button>
          ))}
        </div>
      ))}
      {tx(full && <Lightbox images={images} active={active} setActive={setActive} title={title} onClose={() => setFull(false)} />)}
    </div>
  );
};

const Stars = ({ v }) => { useLang(); return (
  <span className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= Math.round(v) ? "fill-amber-300 text-amber-300" : "text-[var(--mv-border-2)]"}`} />)}</span>
); };

export default function ProductPage() { useLang();
  const { slug } = useParams();
  const { t, formatPrice, requireAuth, wishIds, toggleWish, setCounts, setCartOpen } = useApp();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [sel, setSel] = useState({});
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setP(null); setErr(false); setActive(0);
    api.get(`/products/${slug}`, { params: { via: new URLSearchParams(window.location.search).get("via") || undefined } }).then((r) => {
      setP(r.data); setSel(r.data.variants[0]?.options || {}); setQty(r.data.moq || 1);
      if (r.data.status === "published") {
        api.post("/views", { product_id: r.data.product_id }).catch(() => {});
        const rv = JSON.parse(localStorage.getItem("mivo_recent") || "[]").filter((x) => x !== r.data.product_id);
        localStorage.setItem("mivo_recent", JSON.stringify([r.data.product_id, ...rv].slice(0, 30)));
      }
    }).catch(() => setErr(true));
  }, [slug]);
  const curLang = useLang();
  const firstLang = useRef(curLang);
  useEffect(() => {
    if (firstLang.current === curLang) return;
    firstLang.current = curLang;
    api.get(`/products/${slug}`, { params: { via: new URLSearchParams(window.location.search).get("via") || undefined } }).then((r) => setP(r.data)).catch(() => {});
  }, [curLang]); // eslint-disable-line react-hooks/exhaustive-deps

  const ol = (x) => p?.option_labels?.[x] || tx(x);
  const optionKeys = useMemo(() => (p ? [...new Set(p.variants.flatMap((v) => Object.keys(v.options)))] : []), [p]);
  const variant = useMemo(() => p?.variants.find((v) => optionKeys.every((k) => v.options[k] === sel[k])), [p, sel, optionKeys]);
  const gallery = useMemo(() => (p ? [...p.images, ...[...new Set(p.variants.map((v) => v.image).filter((u) => u && !p.images.includes(u)))]] : []), [p]);

  const pick = (k, val) => {
    const next = { ...sel, [k]: val };
    const match = p.variants.find((v) => optionKeys.every((kk) => v.options[kk] === next[kk])) || p.variants.find((v) => v.options[k] === val);
    setSel(match.options);
    if (match.image) { const i = gallery.indexOf(match.image); if (i >= 0) setActive(i); }
    setQty((q) => Math.min(Math.max(q, p.moq), Math.max(match.stock, p.moq)));
  };

  const add = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/cart/items", { product_id: p.product_id, variant_id: variant.variant_id, quantity: qty, via_store: new URLSearchParams(window.location.search).get("via") || undefined });
      setCounts((c) => ({ ...c, cart: data.count }));
      toast.success(tx(t("product.added")), { action: { label: t("common.cart"), onClick: () => setCartOpen(true) } });
    } catch (e) { toast.error(tx(errMsg(e))); }
    setBusy(false);
  };

  if (err) return <NotFound msg="Product not found" />;
  if (!p) return <div className="mv-container py-24 text-[var(--mv-muted)]">{t("common.loading")}</div>;
  const saved = wishIds.includes(p.product_id);
  const stock = variant?.stock ?? 0;

  return (
    <div className="mv-container pt-8" data-testid="product-page">
      <nav className="mb-6 text-xs text-[var(--mv-dim)]">
        <Link to="/shop" className="hover:text-[var(--mv-teal)]">{t("nav.shop")}</Link> / <Link to={`/shop?category=${p.category_slug}`} className="hover:text-[var(--mv-teal)]">{tx(p.category)}</Link> / {tx(p.subcategory && <><Link to={`/shop?category=${p.subcategory_slug}`} className="hover:text-[var(--mv-teal)]" data-testid="breadcrumb-subcategory">{tx(p.subcategory)}</Link> / </>)}<span className="text-[var(--mv-muted)]">{p.title}</span>
      </nav>
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7"><Gallery images={gallery.length ? gallery : p.images} active={active} setActive={setActive} title={p.title} aiImage={p.ai_image && active === 0} /></div>
        <div className="lg:col-span-5 space-y-6">
          <div>
            {tx(p.is_demo && <span className="mb-3 inline-block rounded-md border border-amber-300/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-300" data-testid="demo-listing-badge">{t("common.demo")}</span>)}
            <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="product-title">{p.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[var(--mv-dim)]">
              <span className="flex items-center gap-1.5" data-testid="product-item-number">{t("product.itemNumber")}: <span className="text-[var(--mv-muted)]">{p.item_number}</span>
                <button aria-label={tx("Copy item number")} onClick={() => { navigator.clipboard?.writeText(p.item_number); toast.success(tx("Copied")); }} data-testid="copy-item-number"><Copy className="h-3.5 w-3.5 hover:text-[var(--mv-teal)]" /></button>
              </span>
              <span data-testid="product-sku">{tx("SKU:")} <span className="text-[var(--mv-muted)]">{variant?.sku}</span></span>
              <span className="flex items-center gap-1.5" data-testid="product-rating">
                {tx(p.review_count > 0 ? <><Stars v={p.rating_avg} /> {tx(p.rating_avg)} ({tx(p.review_count)})</> : t("product.noReviews").split(".")[0])}
              </span>
              {p.sold_count > 0 && <span className="text-[var(--mv-muted)]" data-testid="product-sold-count">{soldLabel(p.sold_count)}</span>}
            </div>
          </div>
          {tx(p.admin_preview && <p className="rounded-lg border border-amber-300/40 bg-amber-300/10 px-3 py-2 text-xs text-amber-200" data-testid="product-admin-preview">{tx("DRAFT PREVIEW — visible only to Super Admin until approved and published")}</p>)}
          <div className="flex items-baseline gap-3">
            <p className="text-3xl font-semibold text-[var(--mv-teal)]" data-testid="product-price">{formatPrice(variant?.price_cents ?? p.price_cents)}</p>
            {tx(p.compare_at_cents > (variant?.price_cents ?? p.price_cents) && <p className="text-base text-[var(--mv-dim)] line-through" data-testid="product-compare-price">{formatPrice(p.compare_at_cents)}</p>)}
          </div>
          {tx(p.highlights?.length > 0 && <ul className="space-y-1.5" data-testid="product-highlights">{p.highlights.map((h) => <li key={h} className="flex gap-2 text-sm text-[var(--mv-muted)]"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--mv-teal)]" />{tx(h)}</li>)}</ul>)}
          {optionKeys.map((k) => (
            <div key={k}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--mv-muted)]">{ol(k)}: <span className="text-[var(--mv-text)] normal-case" data-testid={`variant-selected-${k.replace(/\W+/g, "-").toLowerCase()}`}>{ol(sel[k])}</span></p>
              <div className="flex flex-wrap gap-2">
                {[...new Set(p.variants.map((v) => v.options[k]))].map((val) => {
                  const v = p.variants.find((x) => x.options[k] === val);
                  return (
                    <button key={val} onClick={() => pick(k, val)} data-testid={`variant-option-${val.replace(/\W+/g, "-").toLowerCase()}`}
                      className={`rounded-xl border px-4 py-2 text-sm transition-colors ${sel[k] === val ? "border-[var(--mv-teal)] bg-[var(--mv-teal-dim)] text-[var(--mv-teal)]" : "border-[var(--mv-border-2)] hover:border-[var(--mv-muted)]"} ${v.stock === 0 ? "opacity-40 line-through" : ""}`}>
                      {v.swatch && /colou?r/i.test(k) && <span className="mr-1.5 inline-block h-3 w-3 rounded-full border border-white/30 align-[-1px]" style={{ background: v.swatch }} data-testid={`variant-swatch-${val.replace(/\W+/g, "-").toLowerCase()}`} />}
                      {ol(val)} <span className="ml-1 text-xs text-[var(--mv-dim)]">{formatPrice(v.price_cents)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {tx(p.purchase_block && <p className="rounded-xl border border-amber-300/30 bg-amber-300/5 px-4 py-3 text-xs text-amber-200" data-testid="product-purchase-block">{tx(p.purchase_block)}</p>)}
          <div className="flex items-center gap-4">
            <QtyStepper value={qty} min={p.moq} max={Math.max(stock, p.moq)} onChange={setQty} testid="product-qty" />
            <span className="text-xs text-[var(--mv-dim)]" data-testid="product-stock">{tx(stock > 0 ? `${stock} ${t("product.inStock")}` : t("product.outOfStock"))}{tx(p.moq > 1 ? ` · ${t("product.moq")}: ${p.moq}` : "")}</span>
          </div>
          <div className="flex gap-3">
            <button onClick={add} disabled={busy || stock === 0 || !!p.purchase_block} data-testid="add-to-cart-button" className="mv-btn-primary flex-1 rounded-xl py-4 text-sm disabled:opacity-50">{t("product.addToCart")}</button>
            <button onClick={() => toggleWish(p.product_id)} data-testid="product-favorite-button" aria-pressed={saved} aria-label={t("common.wishlist")}
              className="grid w-14 place-items-center rounded-xl mv-btn-outline"><Heart className={`h-5 w-5 ${saved ? "fill-[var(--mv-teal)] text-[var(--mv-teal)]" : ""}`} /></button>
          </div>
          <DeliveryEstimate productId={p.product_id} />
          {p.recommended_by && <p className="text-xs text-[var(--mv-muted)]" data-testid="product-recommended-by">{tx("Recommended by")} <Link to={`/shops/${p.recommended_by.slug}`} className="font-semibold text-[var(--mv-teal)] hover:underline" data-testid="product-recommended-link">{p.recommended_by.name}</Link></p>}
          <Link to={`/stores/${p.store_info.slug}`} data-testid="product-store-card" className="flex items-center gap-4 rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4 hover:border-[var(--mv-border-2)]">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--mv-teal-dim)]"><Store className="h-5 w-5 text-[var(--mv-teal)]" /></div>
            <div className="flex-1"><p className="text-xs text-[var(--mv-dim)]">{t("product.soldBy")}</p><p className="text-sm font-semibold">{p.store_info.name} {tx("· Grade")} {tx(p.store_info.grade)}</p></div>
            <span className="text-xs text-[var(--mv-teal)]">{t("product.visitStore")}</span>
          </Link>
        </div>
      </div>

      <Tabs defaultValue="details" className="mt-14">
        <TabsList className="bg-[var(--mv-card)] border border-[var(--mv-border)]">
          <TabsTrigger value="details" data-testid="tab-details">{t("product.details")}</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-buying-notes">{t("product.buyingNotes")}</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">{t("product.reviews")} ({tx(p.review_count)})</TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="mt-6 max-w-3xl text-sm leading-relaxed text-[var(--mv-muted)]">
          <p className="whitespace-pre-line">{p.description}</p>
          {tx((p.specs?.length > 0 || p.materials || p.dimensions) && (
            <dl className="mt-6 divide-y divide-[var(--mv-border)] rounded-2xl border border-[var(--mv-border)]" data-testid="product-specs">
              {[...(p.specs || []), ...(p.materials ? [{ label: "Materials", value: p.materials }] : []), ...(p.dimensions ? [{ label: "Dimensions", value: p.dimensions }] : [])].map((x) => (
                <div key={x.label} className="grid grid-cols-3 gap-4 px-4 py-2.5"><dt className="text-[var(--mv-dim)]">{tx(x.label)}</dt><dd className="col-span-2">{x.value}</dd></div>
              ))}
            </dl>
          ))}
          <p className="mt-4 text-xs">{tx("Brand:")} {tx(p.brand || "—")} {tx("· Category:")} {tx(p.category)}{tx(p.subcategory ? ` / ${p.subcategory}` : "")}</p>
        </TabsContent>
        <TabsContent value="notes" className="mt-6 max-w-3xl text-sm leading-relaxed text-[var(--mv-muted)]">
          <p data-testid="product-shipping-info">{tx(p.shipping_info || txf("Shipping, returns and warranty terms are set by {0}.", p.store_info.name))}</p>
          <p className="mt-3">{tx("Prices are settled in USDT with your MIVO card; other currencies are shown for reference only.")}</p>
        </TabsContent>
        <TabsContent value="reviews" className="mt-6 max-w-4xl" data-testid="reviews-section">
          {p.status === "published" ? <ReviewSection product={p} /> : <p className="text-sm text-[var(--mv-muted)]">{tx("Reviews open once the product is published.")}</p>}
        </TabsContent>
      </Tabs>

      {tx(p.related.length > 0 && (
        <section className="mt-16">
          <h2 className="mb-5 text-lg md:text-xl font-medium">{t("product.related")}</h2>
          <ProductGrid items={p.related} />
        </section>
      ))}
    </div>
  );
}
